import { createEnvironmentInjector, DestroyRef, EnvironmentInjector, inject, Injectable, runInInjectionContext, Signal, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { EMPTY, finalize, map, Observable, Subject, tap } from "rxjs";
import { MessageService } from "@app/kernel/tools/message/service/message.service";
import { IMovement } from "@app/shared/fleet/movement/interfaces/movement.interface";
import { EOnboardingSource, EOnboardingStatus, EOnboardingType } from "@app/shared/onboarding/enums/onboarding.enums";
import { IOnboarding } from "@app/shared/onboarding/interfaces/onboarding.interface";
import { IOnboardingSideSection } from "@app/shared/onboarding/interfaces/onboarding-side-section.interface";
import { ONBOARDING_POPUP_OUTLET } from "@app/shared/onboarding/constants/onboarding-popup-outlet.constant";
import { OnboardingApiService } from "@app/shared/onboarding/services/onboarding-api.service";
import { VehicleOnboardingHost } from "@app/shared/onboarding/services/vehicle-onboarding-host.abstraction";
import { CONFIRM_SECTION_COMPONENT, MOVEMENT_ACTION_ONBOARDING_TYPE } from "../constants/movement.constants";
import { ConfirmMovementFormModel } from "../models/confirm-movement-form.model";
import { MovementApiService } from "./movement-api.service";

/**
 * The onboardings of the Close and Confirm actions of the view movement page.
 *
 * Provided on the movements route and bound to the page with {@link connect}, so the page and the onboarding dialog
 * (a routed child of the page) share it; the dialog finds it as its {@link VehicleOnboardingHost}.
 *
 * Close runs the vehicle's check-in onboarding and Confirm its check-out onboarding (`MOVEMENT_ACTION_ONBOARDING_TYPE`).
 * Either one reopens the movement's onboarding of its type while it is STARTED or READY (the dialog loads it by id and
 * updates it), and creates a new one when there is none or the last one is COMMITTED; then it opens the dialog
 * ({@link close}; {@link confirm}, which the page calls once the operator accepted the confirmation alert). When the
 * dialog's last step is submitted ({@link complete}) the movement is closed (check-in) or confirmed (check-out) with one
 * request; the backend commits the onboarding as part of it. A check-out dialog also shows the Confirm section (driver,
 * notes, files; see {@link sideSection}), whose values go with the confirm request. The changed fields of the movement
 * are handed to the page through {@link patched}.
 */
@Injectable()
export class MovementOnboardingFlowService implements VehicleOnboardingHost {
  private readonly onboardingApiService = inject(OnboardingApiService);
  private readonly movementApiService = inject(MovementApiService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly injector = inject(EnvironmentInjector);
  private readonly confirmSectionComponent = inject(CONFIRM_SECTION_COMPONENT);

  /**
   * The onboarding dialog's last button: "Save" for a check-out onboarding, "Save & Close Movement" for a check-in. It
   * follows the type of the movement's latest onboarding, which is the one the dialog edits (also after a reload).
   */
  public get submitLabel(): string {
    return this.movement?.()?.lastOnboarding?.type === MOVEMENT_ACTION_ONBOARDING_TYPE.confirm ? "COMMON.SAVE" : "MOVEMENT.ONBOARDING.SAVE_AND_CLOSE";
  }

  /**
   * The section the check-out dialog shows beside its steps: the Confirm values of the movement, as a form the dialog
   * keeps valid before its last step can be submitted. Null for a check-in. It is built from the movement when first
   * asked (so a reloaded dialog has it too) and rebuilt on every {@link confirm}.
   */
  public get sideSection(): IOnboardingSideSection | null {
    const movement = this.movement?.();
    if (movement?.lastOnboarding?.type !== MOVEMENT_ACTION_ONBOARDING_TYPE.confirm) return null;
    this.confirmSection ??= this.createConfirmSection(movement);
    return { component: this.confirmSectionComponent, form: this.confirmSection.formGroup() };
  }

  /** Whether an onboarding is being created, so repeated clicks are ignored. */
  public readonly starting = signal(false);

  /** Changed fields of the movement, for the page to merge into the movement it shows. */
  public readonly patched = new Subject<Partial<IMovement>>();

  /** The movement the page shows; null while no page is bound. */
  private movement: Signal<IMovement> | null = null;

  /** The view page's route, the parent of the dialog outlet. */
  private route: ActivatedRoute | null = null;

  /** The form of the Confirm section, kept while its dialog may be shown; null until it is first asked for. */
  private confirmSection: ConfirmMovementFormModel | null = null;

  /** The injector the Confirm section's form lives in; destroyed with the form, which frees its watchers. */
  private confirmSectionInjector: EnvironmentInjector | null = null;

  /** The page's lifecycle; every subscription of the flow ends with it. */
  private pageDestroyRef: DestroyRef | null = null;

  /**
   * Binds the flow to the view page; the page calls it once, when it is created.
   *
   * @param movement - The movement the page shows.
   * @param route - The page's route, parent of the onboarding dialog outlet.
   * @param destroyRef - The page's lifecycle.
   */
  public connect(movement: Signal<IMovement>, route: ActivatedRoute, destroyRef: DestroyRef): void {
    this.resetConfirmSection();
    this.movement = movement;
    this.route = route;
    this.pageDestroyRef = destroyRef;
    this.starting.set(false);
    destroyRef.onDestroy(() => {
      // A page created later has bound the flow again: leave its binding alone.
      if (this.pageDestroyRef !== destroyRef) return;
      this.movement = null;
      this.route = null;
      this.pageDestroyRef = null;
    });
  }

  /** Runs the Close action: opens the movement's check-in onboarding (see the class). Errors are shown by the global error toast. */
  public close(): void {
    this.openOrCreate(MOVEMENT_ACTION_ONBOARDING_TYPE.close);
  }

  /**
   * Runs the Confirm action once the operator accepted the alert: opens the movement's check-out onboarding (see the
   * class). The movement itself is confirmed when that onboarding is submitted. Errors are shown by the global error toast.
   */
  public confirm(): void {
    this.resetConfirmSection();
    this.openOrCreate(MOVEMENT_ACTION_ONBOARDING_TYPE.confirm);
  }

  /**
   * The dialog's final step, after its last save: closes the movement (check-in) or confirms it with the values of the
   * Confirm section (check-out). The backend commits the onboarding in the same request.
   *
   * An onboarding that is not READY is not sent: the operator is told to complete the required steps and the dialog
   * stays open. When the request fails the error toast shows and the dialog stays open, so the operator can submit again.
   *
   * @param onboarding - The onboarding as the final save returned it.
   * @returns The changed movement fields, emitted once the step is done.
   */
  public complete(onboarding: IOnboarding): Observable<Partial<IMovement>> {
    const movement = this.movement?.();
    if (!movement || !this.belongsToMovement(onboarding, movement)) return EMPTY;

    if (onboarding.status !== EOnboardingStatus.Ready) {
      this.messageService.warn("MOVEMENT.ONBOARDING.NOT_READY.MESSAGE", "MOVEMENT.ONBOARDING.NOT_READY.TITLE");
      return EMPTY;
    }

    let request$: Observable<Partial<IMovement>>;
    if (onboarding.type === MOVEMENT_ACTION_ONBOARDING_TYPE.confirm) {
      // Only the dialog's own section is sent; without one (it is built when the dialog opens) nothing is.
      const section = this.confirmSection;
      if (!section || section.formGroup().invalid) return EMPTY;
      request$ = this.runLifecycle(
        this.movementApiService.confirmMovement(movement.id, section.getCurrentDtoInput()),
        "MOVEMENT.ACTIONS.CONFIRM.SUCCESS"
      );
    } else {
      request$ = this.runLifecycle(this.movementApiService.closeMovement(movement.id), "MOVEMENT.ACTIONS.CLOSE.SUCCESS");
    }

    return request$.pipe(
      // The backend committed the onboarding with the request: the next action creates a new one instead of reopening it.
      tap(() => this.patched.next({ lastOnboarding: { id: onboarding.id, status: EOnboardingStatus.Committed, type: onboarding.type } }))
    );
  }

  /**
   * Reopens the movement's open onboarding of the type, or creates a new one when there is none or the last one is
   * committed, and opens it in the dialog.
   */
  private openOrCreate(type: EOnboardingType): void {
    const movement = this.movement?.();
    if (!movement || this.starting()) return;

    const existing = movement.lastOnboarding?.type === type ? movement.lastOnboarding : null;

    if (existing && existing.status !== EOnboardingStatus.Committed) {
      this.openDialog(existing.id);
    } else {
      this.startOnboarding(movement, type);
    }
  }

  /** Whether the onboarding is the movement's own, of the Close or the Confirm action (the dialog's URL could name another). */
  private belongsToMovement(onboarding: IOnboarding, movement: IMovement): boolean {
    return (
      onboarding.source === EOnboardingSource.Movement &&
      String(onboarding.sourceId) === String(movement.id) &&
      (Object.values(MOVEMENT_ACTION_ONBOARDING_TYPE) as EOnboardingType[]).includes(onboarding.type)
    );
  }

  /**
   * Creates the movement's onboarding, shows it on the page at once (so the next action reopens it) and opens it in the
   * dialog. The button stays disabled meanwhile, so one click creates one onboarding.
   */
  private startOnboarding(movement: IMovement, type: EOnboardingType): void {
    const input = { vehicle: String(movement.vehicle.id), type, source: EOnboardingSource.Movement, sourceId: String(movement.id) };
    this.runWhileStarting(this.onboardingApiService.createOnboarding(input), onboarding => {
      this.patched.next({ lastOnboarding: { id: onboarding.id, status: onboarding.status, type: onboarding.type } });
      this.openDialog(onboarding.id);
    });
  }

  /** Runs a step while {@link starting} is set, ending with the page. Errors are shown by the global error toast. */
  private runWhileStarting<T>(step: Observable<T>, onDone?: (value: T) => void): void {
    this.starting.set(true);
    step
      .pipe(
        finalize(() => this.starting.set(false)),
        takeUntilDestroyed(this.pageDestroyRef!)
      )
      .subscribe({ next: onDone, error: () => undefined });
  }

  /** Builds the Confirm section's form from the movement (its driver is prefilled). */
  private createConfirmSection(movement: IMovement): ConfirmMovementFormModel {
    this.confirmSectionInjector = createEnvironmentInjector([], this.injector);
    const model = runInInjectionContext(this.confirmSectionInjector, () => new ConfirmMovementFormModel());
    model.setEntity(movement);
    return model;
  }

  /** Drops the Confirm section's form, so the next one starts from the movement again. */
  private resetConfirmSection(): void {
    this.confirmSection?.destroy();
    // The kernel sets up a form's validator watchers in a microtask, so a form dropped right away is released after it.
    const injector = this.confirmSectionInjector;
    if (injector) setTimeout(() => injector.destroy());
    this.confirmSection = null;
    this.confirmSectionInjector = null;
  }

  /** Sends the closing or confirming request, hands the changed fields to the page and shows the success toast. */
  private runLifecycle(request: Observable<Partial<IMovement> | undefined>, successMessage: string): Observable<Partial<IMovement>> {
    return request.pipe(
      map(updated => updated ?? {}),
      tap(updated => {
        this.patched.next(updated);
        this.messageService.success(successMessage, "COMMON.SUCCESS_FEEDBACK.TITLE");
      })
    );
  }

  /** Opens the onboarding dialog in the view page's outlet; the page's query params are kept. */
  private openDialog(onboardingId: string): void {
    this.router.navigate([{ outlets: { [ONBOARDING_POPUP_OUTLET]: ["edit", onboardingId] } }], {
      relativeTo: this.route,
      queryParamsHandling: "preserve",
    });
  }
}
